import { V1_ADDON_DISPLAY_NAME_MAP } from "@/components/Checkout/components/migration/data";
import { Button } from "@/components/ui/button";
import { LoaderSpinner } from "@/components/ui/Loader/Spinner";
import formatTemplateString from "@/utils/formatTemplateString";
import { DashboardService } from "../service.dashboard";
import {
  TDashboardSubscriptionListContent,
  TPlanActionType,
} from "../types.dashboard";
import { ComponentCard, ComponentHeader, type StatusBadgeVariant } from "../ui";
import { ListBlock, type ListBlockValueItem } from "../ui/ListBlock";
import { TDashboardHandlers, TDashboardUserPlan } from "../useDashboard";
import {
  getAddOnsDetail,
  getSubscriptionDateInfo,
  getSubscriptionStatusInfo,
} from "../utils";

interface ManageButtonStrategy {
  label: string;
  variant: "accent" | "outline" | "dark";
  size: "sm";
  disabled: boolean;
  onClick: () => void;
}

function getPrimaryCTAStrategy({
  planActionType,
  loading,
  isReactivating,
  isResuming,
  handlers,
  copy,
}: {
  planActionType: TPlanActionType;
  loading: boolean;
  isReactivating: boolean;
  isResuming: boolean;
  copy: TDashboardSubscriptionListContent["actions"];
  handlers: Pick<
    TDashboardHandlers,
    | "handlePlanManage"
    | "handlePlanPurchase"
    | "handleReactivate"
    | "handleMigration"
    | "handleResume"
    | "handleCancelPlan"
  >;
}): ManageButtonStrategy {
  const {
    handlePlanManage,
    handlePlanPurchase,
    handleReactivate,
    handleMigration,
    handleResume,
    handleCancelPlan,
  } = handlers;

  switch (planActionType) {
    case "cancelling":
      return {
        label: isReactivating ? copy.reactivating : copy.reactivate,
        variant: "accent",
        size: "sm",
        disabled: loading || isReactivating,
        onClick: () => handleReactivate(),
      };
    case "paused":
      return {
        label: isResuming ? copy.resuming : copy.resume,
        variant: "accent",
        size: "sm",
        disabled: loading || isResuming,
        onClick: () => handleResume(),
      };
    case "purchase":
      return {
        label: copy.purchase,
        variant: "accent",
        size: "sm",
        disabled: loading,
        onClick: () => handlePlanPurchase(),
      };
    case "trialing":
      return {
        label: copy.manage,
        variant: "accent",
        size: "sm",
        disabled: loading,
        onClick: () => handlePlanPurchase(),
      };
    case "migrate":
      return {
        label: copy.manage,
        variant: "accent",
        size: "sm",
        disabled: loading,
        onClick: () => handleMigration(),
      };
    case "cancel":
      return {
        label: copy.manage,
        variant: "dark",
        size: "sm",
        disabled: loading,
        onClick: () => handleMigration(),
      };
    case "main-cancel":
      return {
        label: copy.cancel,
        variant: "accent",
        size: "sm",
        disabled: loading,
        onClick: () => handleCancelPlan(),
      };
    default:
      return {
        label: copy.manage,
        variant: "accent",
        size: "sm",
        disabled: loading,
        onClick: () => handlePlanManage(),
      };
  }
}

interface SubscriptionInfo {
  id: string;
  label: string;
  value: string | ListBlockValueItem[];
  theme?: StatusBadgeVariant;
  isStatus?: boolean;
}

interface SubscriptionDetailProps {
  userPlan: TDashboardUserPlan;
  planActionType: TPlanActionType;
  loading: boolean;
  isReactivating: boolean;
  isResuming: boolean;
  handlers: TDashboardHandlers;
  onPrimaryActionClick?: () => void;
}

const SubscriptionList = ({
  userPlan,
  planActionType,
  loading,
  isReactivating,
  isResuming,
  handlers,
  onPrimaryActionClick,
}: SubscriptionDetailProps) => {
  const COPY = DashboardService.Content.dashboardSubscriptionList;

  const {
    planStatus,
    billingInterval,
    cancelAt,
    totalCustomAiProfiles,
    totalInstantAiProfiles,
    currentPeriodEnd,
    resumeAt,
    ownedAddOnItemList,
    v1AddOnList,
    activePlanNames,
    IS_USER_ON_PRICING_V2: isUserUsingPricingV2,
  } = userPlan;

  // Frontend-only: founders are on the Max plan but are labelled "Founders" in the UI.
  const planNames = activePlanNames;

  const todayTimestamp = Date.now();

  const addOnsDetail = isUserUsingPricingV2
    ? getAddOnsDetail({
        ownedAddOnItemList,
        customAiProfile: totalCustomAiProfiles,
        instantProfiles: totalInstantAiProfiles,
      })
    : v1AddOnList.map((addon) =>
        formatTemplateString(
          COPY.addOns.v1AddOn,
          V1_ADDON_DISPLAY_NAME_MAP[addon.id] ?? addon.name,
          String(addon.quantity),
        ),
      );

  const { label: dateLabel, value: dateValue } = getSubscriptionDateInfo({
    planStatus,
    currentPeriodEnd,
    cancelAt,
    resumeAt,
    todayTimestamp,
  });

  const statusInfo = getSubscriptionStatusInfo({
    planStatus,
    cancelAt,
    todayTimestamp,
  });

  const displayedPlanNames: ListBlockValueItem[] =
    planNames.length > 0 ? planNames : [COPY.emptyValue];

  const planTypeLabel = billingInterval
    ? COPY.planType[billingInterval]
    : COPY.emptyValue;

  const subscriptionInfo: SubscriptionInfo[] = [
    {
      id: "subscriptionInfo_1",
      label: COPY.labels.plans,
      value: displayedPlanNames,
    },
    {
      id: "subscriptionInfo_2",
      label: COPY.labels.addOns,
      value: addOnsDetail.length > 0 ? addOnsDetail : [COPY.emptyValue],
    },
    {
      id: "subscriptionInfo_3",
      label: COPY.labels.planType,
      value: planTypeLabel,
    },
    ...(statusInfo ? [statusInfo] : []),
    {
      id: "subscriptionInfo_4",
      label: dateLabel,
      value: dateValue,
    },
  ];

  const {
    label: primaryCTALabel,
    onClick: strategyOnClick,
    ...strategyProps
  } = getPrimaryCTAStrategy({
    planActionType,
    loading,
    isReactivating,
    isResuming,
    handlers,
    copy: COPY.actions,
  });
  const primaryCTAProps = {
    ...strategyProps,
    onClick: onPrimaryActionClick ?? strategyOnClick,
  };

  return (
    <div className="space-y-5 bg-white">
      {/* header */}
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between">
        <ComponentHeader text={COPY.title} />

        <div className="hidden items-center justify-end gap-5 md:flex">
          <Button
            disabled={loading}
            variant="link"
            size="sm"
            onClick={handlers.handlePaymentDetailsClick}
            className="text-sm leading-5 font-medium"
          >
            {COPY.actions.paymentDetails}
          </Button>

          <Button
            {...primaryCTAProps}
            className="text-sm leading-5 font-medium"
          >
            {primaryCTALabel}
          </Button>
        </div>
      </div>

      {/* active plans */}
      <ComponentCard className="relative grid h-full w-full grid-cols-2 items-start gap-6 space-y-0 md:flex md:justify-between md:gap-4">
        {loading && (
          <div className="absolute inset-0 z-20 flex cursor-not-allowed items-center justify-center rounded-xl bg-white/5 backdrop-blur-[0.5px]">
            <div className="animate-shimmer h-full w-full bg-linear-to-r from-transparent via-white/40 to-transparent bg-size-[200%_100%]" />
            <LoaderSpinner className="text-accent-blue absolute top-1 left-1 h-6 w-6" />
          </div>
        )}

        {subscriptionInfo.map((info, idx) => (
          <ListBlock
            key={info.id}
            label={info.label}
            value={info.value}
            theme={info.theme}
            sn={idx + 1}
          />
        ))}
      </ComponentCard>

      {/* Action Buttons mobile */}
      <div className="flex w-full items-center justify-between gap-4 md:hidden">
        <Button
          variant="outline"
          disabled={loading}
          size="sm"
          onClick={handlers.handlePaymentDetailsClick}
          className="flex-1 text-sm leading-5 font-medium"
        >
          {COPY.actions.paymentDetails}
        </Button>

        <Button
          {...primaryCTAProps}
          className="flex-1 text-sm leading-5 font-medium"
        >
          {primaryCTALabel}
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionList;
